import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { type LayoutChangeEvent, Modal, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Gesture, GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
	Easing,
	Extrapolation,
	interpolate,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { SvgIconType } from '@thermenresorts/icons';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import NavigationButton from 'src/presentation/components/molecules/NavigationButton';

const SETTLE_ANIMATION_TIMING = { duration: Dimens.animationDuration500, easing: Easing.out(Easing.exp) };
const DISMISS_ANIMATION_TIMING = { duration: Dimens.animationDuration250, easing: Easing.in(Easing.cubic) };

interface Props {
	visible: boolean;
	onClose: () => void;
	onBack?: () => void;
	contentKey?: string;
	children: ReactNode;
}

const BottomSheet: FC<Props> = ({ visible, onClose, onBack, contentKey, children }: Props) => {
	const { height: windowHeight } = useWindowDimensions();
	const scroll = useRef<ScrollView>(null);
	const insets = useSafeAreaInsets();
	const offset = useSharedValue(windowHeight);
	const sheetHeight = useSharedValue(Dimens.size0);

	const close = useCallback(() => {
		if (sheetHeight.value === Dimens.size0) {
			onClose();

			return;
		}

		offset.value = withTiming(sheetHeight.value, DISMISS_ANIMATION_TIMING, (finished) => {
			if (finished === true) {
				scheduleOnRN(onClose);
			}
		});
	}, [offset, onClose, sheetHeight]);

	// The android back button belongs to the step the sheet shows, so a sheet with a step above it
	// goes back there and only a sheet on its first step is dismissed.
	const requestClose = useCallback(() => {
		if (onBack === undefined) {
			close();

			return;
		}

		onBack();
	}, [close, onBack]);

	const measureSheet = ({ nativeEvent }: LayoutChangeEvent) => {
		const height = nativeEvent.layout.height;
		const isFirstMeasure = sheetHeight.value === Dimens.size0;

		sheetHeight.value = height;

		if (isFirstMeasure) {
			offset.value = height;
			offset.value = withTiming(Dimens.size0, SETTLE_ANIMATION_TIMING);
		}
	};

	// A sheet that swaps its content keeps one scroll view, so the next content would open halfway down.
	useEffect(() => {
		scroll.current?.scrollTo({ y: Dimens.size0, animated: false });
	}, [contentKey]);

	useEffect(() => {
		if (!visible) {
			sheetHeight.value = Dimens.size0;
			offset.value = windowHeight;
		}
	}, [offset, sheetHeight, visible, windowHeight]);

	const dragTo = useCallback(
		(translationY: number) => {
			'worklet';

			offset.value = Math.max(translationY, Dimens.size0);
		},
		[offset],
	);

	// A gesture the system takes away ends with success false, which is a sheet nobody let go of.
	const releaseDrag = useCallback(
		(velocityY: number, success: boolean) => {
			'worklet';

			const draggedFarEnough = offset.value > sheetHeight.value * Dimens.ratio25;
			const flungDown = velocityY > Dimens.swipeCloseVelocity800;

			if (success && (draggedFarEnough || flungDown)) {
				scheduleOnRN(close);

				return;
			}

			offset.value = withTiming(Dimens.size0, SETTLE_ANIMATION_TIMING);
		},
		[close, offset, sheetHeight],
	);

	const drag = useMemo(
		() =>
			Gesture.Pan()
				.hitSlop({ bottom: Dimens.padding12 })
				.onUpdate((event) => dragTo(event.translationY))
				.onEnd((event, success) => releaseDrag(event.velocityY, success)),
		[dragTo, releaseDrag],
	);

	// The scrim drags the sheet along, so a swipe above the sheet dismisses it as well as a tap does.
	// The tap is a gesture rather than a Pressable: a Pressable decides on its own, so a drag that ends
	// back where it started would still read as a press and close a sheet the member just pulled back.
	const scrimDrag = useMemo(
		() =>
			Gesture.Race(
				Gesture.Pan()
					.onUpdate((event) => dragTo(event.translationY))
					.onEnd((event, success) => releaseDrag(event.velocityY, success)),
				Gesture.Tap().onEnd((_event, success) => {
					if (success) {
						scheduleOnRN(close);
					}
				}),
			),
		[close, dragTo, releaseDrag],
	);

	const sheetStyle = useAnimatedStyle(() => ({ transform: [{ translateY: offset.value }] }), [offset]);

	const backdropStyle = useAnimatedStyle(
		() => ({
			opacity:
				sheetHeight.value === Dimens.size0
					? Dimens.opacity0
					: interpolate(offset.value, [Dimens.size0, sheetHeight.value], [Dimens.opacity100, Dimens.opacity0], {
							extrapolateRight: Extrapolation.CLAMP,
						}),
		}),
		[offset, sheetHeight],
	);

	return (
		<Modal animationType="none" transparent={true} visible={visible} onRequestClose={requestClose}>
			<GestureHandlerRootView style={styles.root}>
				<Animated.View style={[styles.backdrop, backdropStyle]} />
				<GestureDetector gesture={scrimDrag}>
					<View accessible={false} importantForAccessibility="no" style={styles.scrimFill} />
				</GestureDetector>
				<Animated.View
					style={[styles.sheet, { maxHeight: windowHeight - Dimens.size100, paddingBottom: insets.bottom }, sheetStyle]}
					onLayout={measureSheet}
				>
					<GestureDetector gesture={drag}>
						<View style={styles.grabberBand}>
							<View style={styles.grabber} />
						</View>
					</GestureDetector>
					<NavigationButton
						accessibilityLabel={Translations.close}
						icon={SvgIconType.cross}
						iconColor={Colors.gray800}
						iconSize={Dimens.iconSize12}
						style={styles.close}
						onPress={close}
					/>
					<ScrollView contentContainerStyle={styles.content} ref={scroll} style={styles.scroll}>
						{children}
					</ScrollView>
				</Animated.View>
			</GestureHandlerRootView>
		</Modal>
	);
};

const styles = StyleSheet.create({
	root: {
		flex: Dimens.flex1,
		justifyContent: 'flex-end',
	},
	backdrop: {
		...StyleSheet.absoluteFill,
		backgroundColor: Colors.black66,
	},
	scrimFill: {
		flex: Dimens.flex1,
	},
	sheet: {
		backgroundColor: Colors.white,
		borderTopLeftRadius: Dimens.borderRadius16,
		borderTopRightRadius: Dimens.borderRadius16,
		paddingHorizontal: Dimens.padding18,
		gap: Dimens.gap14,
	},
	grabberBand: {
		alignItems: 'center',
		paddingTop: Dimens.padding20,
		paddingBottom: Dimens.padding20,
	},
	grabber: {
		width: Dimens.size134,
		height: Dimens.size5,
		borderRadius: Dimens.borderRadiusPill,
		backgroundColor: Colors.gray750,
	},
	close: {
		position: 'absolute',
		right: Dimens.padding8,
		top: Dimens.padding8,
		zIndex: Dimens.zIndexRaised,
	},
	scroll: {
		flexGrow: Dimens.flex0,
		flexShrink: Dimens.flex1,
		marginHorizontal: Dimens.paddingMinus18,
	},
	content: {
		gap: Dimens.gap14,
		paddingBottom: Dimens.padding24,
		paddingHorizontal: Dimens.padding18,
	},
});

export default BottomSheet;
